// ============================================================================
// Shared marketplace preferences store — persists to localStorage
// ============================================================================

export interface MarketplacePreference {
  code: string
  flag: string
  name: string
  domain: string
  enabled: boolean
}

const STORAGE_KEY = 'aplus-marketplace-preferences'

// Default marketplace list — all enabled except AU and IN
const DEFAULT_PREFERENCES: MarketplacePreference[] = [
  { code: 'US', flag: '🇺🇸', name: 'United States', domain: 'amazon.com', enabled: true },
  { code: 'CA', flag: '🇨🇦', name: 'Canada', domain: 'amazon.ca', enabled: true },
  { code: 'MX', flag: '🇲🇽', name: 'Mexico', domain: 'amazon.com.mx', enabled: true },
  { code: 'UK', flag: '🇬🇧', name: 'United Kingdom', domain: 'amazon.co.uk', enabled: true },
  { code: 'DE', flag: '🇩🇪', name: 'Germany', domain: 'amazon.de', enabled: true },
  { code: 'FR', flag: '🇫🇷', name: 'France', domain: 'amazon.fr', enabled: true },
  { code: 'IT', flag: '🇮🇹', name: 'Italy', domain: 'amazon.it', enabled: true },
  { code: 'ES', flag: '🇪🇸', name: 'Spain', domain: 'amazon.es', enabled: true },
  { code: 'JP', flag: '🇯🇵', name: 'Japan', domain: 'amazon.co.jp', enabled: true },
  { code: 'AU', flag: '🇦🇺', name: 'Australia', domain: 'amazon.com.au', enabled: false },
  { code: 'IN', flag: '🇮🇳', name: 'India', domain: 'amazon.in', enabled: false },
]

function load(): MarketplacePreference[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (raw) return JSON.parse(raw)
  } catch {}
  // First time — seed with defaults
  save(DEFAULT_PREFERENCES)
  return DEFAULT_PREFERENCES
}

function save(prefs: MarketplacePreference[]) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(prefs))
}

/** Get all marketplace preferences (including disabled ones), in saved order */
export function getAllMarketplaces(): MarketplacePreference[] {
  return load()
}

/** Get only the enabled marketplaces, in saved order — use this for table/card views */
export function getEnabledMarketplaces(): MarketplacePreference[] {
  return load().filter((m) => m.enabled)
}

/** Save the full preferences list (order + enabled state) */
export function saveMarketplacePreferences(prefs: MarketplacePreference[]) {
  save(prefs)
}

/** Toggle a single marketplace's enabled state */
export function toggleMarketplace(code: string) {
  const prefs = load()
  const updated = prefs.map((m) => (m.code === code ? { ...m, enabled: !m.enabled } : m))
  save(updated)
  return updated
}

/** Reorder marketplaces — move item at fromIdx to toIdx */
export function reorderMarketplaces(fromIdx: number, toIdx: number): MarketplacePreference[] {
  const prefs = load()
  const next = [...prefs]
  const [moved] = next.splice(fromIdx, 1)
  next.splice(toIdx, 0, moved)
  save(next)
  return next
}
